<template>
  <div>
    <Navbar />
    <div class="news-page">

      <!-- Banner -->
      <div class="banner">
        <h1 class="banner-title">News & Events</h1>
        <p class="banner-subtitle">الأخبار والفعاليات</p>
      </div>

      <!-- Category Filter -->
      <div v-if="categories.length > 1" class="filter-bar">
        <button :class="['filter-btn', { active: activeCategory === '' }]" @click="activeCategory = ''">All</button>
        <button
          v-for="cat in categories" :key="cat"
          :class="['filter-btn', { active: activeCategory === cat }]"
          @click="activeCategory = cat"
        >{{ cat }}</button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading news…</p>
      </div>

      <!-- News Grid -->
      <div v-else-if="filteredNews.length" class="news-grid">
        <article
          v-for="(item, i) in filteredNews"
          :key="item.id || i"
          class="news-card"
          :class="{ featured: i === 0 && !activeCategory }"
          :style="{ animationDelay: i * 60 + 'ms' }"
          @click="openDetail(item)"
        >
          <div class="card-image">
            <img v-if="item.image" :src="item.image" :alt="item.title" />
            <div v-else class="image-fallback">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="48" height="48"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </div>
            <span v-if="item.category" class="card-badge">{{ item.category }}</span>
          </div>
          <div class="card-body">
            <time v-if="item.date" class="card-date">{{ formatDate(item.date) }}</time>
            <h3 class="card-title">{{ item.title }}</h3>
            <p v-if="item.excerpt" class="card-excerpt">{{ item.excerpt }}</p>
            <span class="card-read-more">Read more →</span>
          </div>
        </article>
      </div>

      <!-- Empty -->
      <div v-else-if="!loading" class="empty-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5" width="64" height="64"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        <p>No news or events found.</p>
      </div>

      <!-- Detail Modal -->
      <Teleport to="body">
        <div v-if="detailItem" class="modal-backdrop" @click.self="detailItem = null">
          <div class="modal-content">
            <button class="modal-close" @click="detailItem = null">×</button>
            <img v-if="detailItem.image" :src="detailItem.image" :alt="detailItem.title" class="modal-image" />
            <div class="modal-body">
              <span v-if="detailItem.category" class="modal-badge">{{ detailItem.category }}</span>
              <time v-if="detailItem.date" class="modal-date">{{ formatDate(detailItem.date) }}</time>
              <h2 class="modal-title">{{ detailItem.title }}</h2>
              <div v-if="detailItem.body" class="modal-text" v-html="detailItem.body"></div>
              <p v-else-if="detailItem.excerpt" class="modal-text">{{ detailItem.excerpt }}</p>
            </div>
          </div>
        </div>
      </Teleport>

    </div>
    <Footer />
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import api from '@/services/api'
import { mediaUrl, resolveRichText } from '@/services/media'

export default {
  name: 'NewsPage',
  components: { Navbar, Footer },
  data() {
    return {
      loading: true,
      allNews: [],
      activeCategory: '',
      categories: [],
      detailItem: null,
    }
  },
  computed: {
    filteredNews() {
      if (!this.activeCategory) return this.allNews;
      return this.allNews.filter(n => n.category === this.activeCategory);
    }
  },
  async created() {
    document.title = 'News & Events | Al Shola School';
    try {
      const data = await api.getNews();
      const list = api.extractList(data);
      this.allNews = list.map(n => ({
        id: n.id,
        title: n.title || '',
        excerpt: n.excerpt || n.summary || this.stripHtml(n.content || n.body || '').substring(0, 160),
        body: resolveRichText(n.content || n.body || ''),
        image: n.image ? mediaUrl(n.image) : (n.cover_image ? mediaUrl(n.cover_image) : ''),
        date: n.published_at || n.date || n.created_at || '',
        category: n.category || n.event_type || '',
        slug: n.slug || '',
      }));
      this.allNews.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

      const cats = new Set();
      this.allNews.forEach(n => { if (n.category) cats.add(n.category); });
      this.categories = [...cats].sort();
    } catch (e) {
      console.warn('[News] API error:', e.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      } catch { return dateStr; }
    },
    stripHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || '';
    },
    async openDetail(item) {
      if (item.slug && !item.body) {
        try {
          const detail = await api.getNewsDetail(item.slug);
          item.body = resolveRichText(detail.content || detail.body || '');
          if (detail.image) item.image = mediaUrl(detail.image);
        } catch (e) {
          console.warn('[News] Detail fetch failed:', e.message);
        }
      }
      this.detailItem = item;
    }
  }
}
</script>

<style scoped>
* { box-sizing: border-box; }

.news-page { min-height: 60vh; background: #fafafa; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; position: relative; overflow: visible; }

/* Banner */
.banner { background: linear-gradient(135deg, #C02130 0%, #8B1722 100%); padding: 60px 24px; text-align: center; position: relative; z-index: 1; }
.banner-title { color: #fff; font-size: clamp(28px, 5vw, 42px); font-weight: 800; margin: 0 0 6px; }
.banner-subtitle { color: rgba(255,255,255,0.85); font-size: 20px; margin: 0; font-family: 'Arial', sans-serif; }

/* Filter */
.filter-bar { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; padding: 32px 24px; max-width: 900px; margin: 0 auto; position: relative; z-index: 2; }
.filter-btn { padding: 8px 20px; border: 2px solid #e0e0e0; border-radius: 24px; background: #fff; color: #555; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.25s; }
.filter-btn:hover { border-color: #C02130; color: #C02130; }
.filter-btn.active { background: #C02130; border-color: #C02130; color: #fff; }

/* Loading / Empty */
.loading-state { display: flex; flex-direction: column; align-items: center; padding: 80px 20px; gap: 16px; color: #888; position: relative; z-index: 2; }
.spinner { width: 40px; height: 40px; border: 3px solid #eee; border-top-color: #C02130; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.empty-state { padding: 80px 20px; text-align: center; color: #bbb; display: flex; flex-direction: column; align-items: center; gap: 16px; position: relative; z-index: 2; }

/* Grid */
.news-grid {
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 24px 60px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  position: relative;
  z-index: 2;
}

/* Featured first card spans full width */
.news-card.featured { grid-column: 1 / -1; display: flex; flex-direction: row; }
.news-card.featured .card-image { width: 50%; aspect-ratio: auto; height: 320px; flex-shrink: 0; }
.news-card.featured .card-image img { object-fit: contain; object-position: center top; }
.news-card.featured .card-body { flex: 1; display: flex; flex-direction: column; justify-content: center; padding: 32px; }
.news-card.featured .card-title { font-size: 24px; }

/* Card */
.news-card {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border: 1px solid #eee;
  cursor: pointer;
  transition: all 0.3s;
  animation: fadeUp 0.5s ease both;
  position: relative;
  z-index: 2;
}
.news-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.12); border-color: #C02130; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* Card Image — square with full image visible */
.card-image {
  position: relative;
  aspect-ratio: 1 / 1;
  background: #f5f5f5;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.card-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center top;
  transition: transform 0.3s;
  display: block;
}
.news-card:hover .card-image img { transform: scale(1.03); }
.image-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #f5f5f5, #eee); color: #ccc; }

.card-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #C02130;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 3;
}

.card-body { padding: 20px; }
.card-date { font-size: 12px; color: #999; display: block; margin-bottom: 8px; }
.card-title { font-size: 18px; font-weight: 700; color: #1a1a1a; margin: 0 0 8px; line-height: 1.35; }
.card-excerpt { font-size: 14px; color: #666; line-height: 1.6; margin: 0 0 12px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.card-read-more { font-size: 13px; font-weight: 600; color: #C02130; }

/* Modal */
.modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 9999;
  display: flex; align-items: center; justify-content: center; padding: 20px;
  animation: fadeIn 0.2s ease;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.modal-content {
  background: #fff; border-radius: 16px; max-width: 700px; width: 100%;
  max-height: 85vh; overflow-y: auto; position: relative;
  box-shadow: 0 24px 64px rgba(0,0,0,0.3);
  animation: slideUp 0.3s ease;
}
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

.modal-close {
  position: absolute; top: 12px; right: 12px; z-index: 10;
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: rgba(0,0,0,0.5); color: #fff; font-size: 22px;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
}
.modal-close:hover { background: #C02130; }

.modal-image { width: 100%; height: 280px; object-fit: cover; }
.modal-body { padding: 28px; }
.modal-badge { display: inline-block; background: #FFF3F3; color: #C02130; font-size: 12px; font-weight: 700; padding: 4px 14px; border-radius: 16px; margin-bottom: 8px; }
.modal-date { display: block; font-size: 13px; color: #999; margin-bottom: 12px; }
.modal-title { font-size: 24px; font-weight: 800; color: #1a1a1a; margin: 0 0 16px; line-height: 1.3; }
.modal-text { font-size: 15px; color: #444; line-height: 1.8; }
.modal-text :deep(img) { max-width: 100%; border-radius: 8px; margin: 12px 0; }

@media (max-width: 768px) {
  .news-grid { grid-template-columns: 1fr; padding: 24px 16px 40px; }
  .news-card.featured { flex-direction: column; }
  .news-card.featured .card-image { width: 100%; height: auto; aspect-ratio: 1 / 1; }
  .news-card.featured .card-body { padding: 20px; }
  .banner { padding: 40px 20px; }
  .modal-content { max-height: 90vh; border-radius: 12px; }
}
</style>